// Microsoft 第三方服務整合 API 服務模組
// 呼叫 mygenieap 的 /web/integrations/microsoft/* 端點（status / authorize-url / disconnect）。
import apiService from './apiService';
import * as appStorage from '../utils/appIndexedDB';
import logger from '../utils/logger';
import { decodeJwtPayload } from '../utils/pcsUser';

const TOKEN_STORAGE_KEY = 'mygenie_token';
const REFRESH_TOKEN_STORAGE_KEY = 'mygenie_refresh_token';

/**
 * 帶身分驗證呼叫 /web/integrations/microsoft/*，業務碼 401（token 過期）時自動 refresh 並重送一次，
 * 沿用 apiService 既有的 refresh/重新登入流程（與 scheduleService.authorizedRequest 一致）。
 * 回傳 mygenieap 的 ApiResponse 原樣（{ code, message, data }），非 401 的業務錯誤交由呼叫端判斷。
 */
export async function authorizedRequest(path, { method = 'GET', body } = {}) {
  const accessToken = await appStorage.getItem(TOKEN_STORAGE_KEY);
  if (!accessToken) {
    throw new Error('尚未登入或 token 遺失，請重新登入');
  }

  const doCall = (token) =>
    apiService.request(path, {
      method,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${token}`,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

  const response = await doCall(accessToken);
  if (response?.code !== 401) return response;

  logger.warn('第三方服務', 'Microsoft 整合 API 命中業務碼 401，進入 refresh token 流程', { path });
  const refreshToken = await appStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  if (!refreshToken) {
    apiService.clearAuthAndRedirectToLogin();
    throw new Error('登入已過期，請重新登入');
  }
  const refreshResult = await apiService.refreshToken(refreshToken);
  if (!refreshResult.success) {
    // 網路瞬斷不代表 refresh token 失效，不跳重新登入（同 apiService.refreshToken 的 networkError 判斷）
    if (!refreshResult.networkError) {
      apiService.showReauthRequiredDialog(() => apiService.clearAuthAndRedirectToLogin());
    }
    throw new Error('登入已過期，請重新登入');
  }
  return doCall(refreshResult.token);
}

/**
 * 登入 token 快過期就先用 refresh token 換新的（信件即時通知新增表單開著時用，10-20）。
 *
 * 以 JWT 的 `exp` 判斷，不寫死間隔：效期是後端環境變數 JWT_EXPIRATION（預設 60 分鐘），各環境可能不同。
 * ⛔ 失敗時靜默，不跳重新登入對話框：表單開著時跳出來、按確定會重新整理，填到一半的內容就沒了；
 * 交給使用者按儲存的那一次（authorizedRequest 遇 401 會 refresh）處理。
 *
 * @param {number} thresholdMs 剩餘效期小於這個值才換
 * @returns {Promise<'fresh'|'refreshed'|'failed'>}
 */
export async function refreshTokenIfExpiringSoon(thresholdMs) {
  try {
    const accessToken = await appStorage.getItem(TOKEN_STORAGE_KEY);
    if (!accessToken) return 'failed';
    const exp = decodeJwtPayload(accessToken)?.exp;
    if (typeof exp === 'number' && exp * 1000 - Date.now() > thresholdMs) return 'fresh';

    const refreshToken = await appStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (!refreshToken) return 'failed';
    const result = await apiService.refreshToken(refreshToken);
    if (result.success) return 'refreshed';
    logger.warn('第三方服務', '登入 token 主動換新失敗，交給下一次操作處理', { networkError: result.networkError });
    return 'failed';
  } catch (e) {
    logger.warn('第三方服務', '登入 token 主動換新發生例外', { error: e?.message });
    return 'failed';
  }
}

/** 3-1 查詢所有 Microsoft type 連線狀態，回應 data.types 為各 type 的狀態 */
export function getMicrosoftStatus() {
  return authorizedRequest('/web/integrations/microsoft/status');
}

/** 3-4 取得指定 type 的 Microsoft OAuth 授權 URL，回應 data.url */
export function getMicrosoftAuthorizeUrl(type) {
  return authorizedRequest(`/web/integrations/microsoft/authorize-url?type=${encodeURIComponent(type)}`);
}

/** 3-3 中斷指定 type 連線 */
export function disconnectMicrosoft(type) {
  return authorizedRequest(`/web/integrations/microsoft/disconnect?type=${encodeURIComponent(type)}`, {
    method: 'POST',
  });
}
